/**
 * "Edit song" dialog — title and genre.
 *
 * Draft values live here rather than in a store: they are transient form state
 * that only matters while the dialog is open, and discarding them on cancel is the
 * intended behaviour. The dialog is keyed by song id so reopening it on a
 * different song reinitialises the fields.
 */
import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button, Modal } from '../ui';
import { Select, TextField } from '../forms';
import { GENRES } from '../../data/music';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface EditSongModalProps {
  /** The song being edited, or null when closed. */
  song: Song | null;
  onClose: () => void;
  onSave: (changes: { title: string; genre: string }) => void;
}

function EditSongForm({ song, onClose, onSave }: EditSongModalProps & { song: Song }) {
  const [title, setTitle] = useState(song.title);
  const [genre, setGenre] = useState(song.genre);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <p style={{ fontWeight: 600, fontSize: 15, margin: 0, fontFamily: 'Space Grotesk' }}>
          Edit song
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          aria-label="Close"
        >
          <X size={16} color={c.text2} />
        </button>
      </div>

      <TextField
        id="lyx-edit-title"
        label="Title"
        value={title}
        onChange={setTitle}
        marginBottom={12}
        style={{ padding: '8px 10px' }}
      />
      <Select
        id="lyx-edit-genre"
        label="Genre"
        value={genre}
        onChange={setGenre}
        options={GENRES}
        marginBottom={18}
        style={{ padding: '8px 10px' }}
      />

      <Button full onClick={() => onSave({ title, genre })} style={{ marginBottom: 8 }}>
        <Check size={13} />
        Save changes
      </Button>
      <Button full variant="ghost" onClick={onClose}>
        Cancel
      </Button>
    </>
  );
}

export default function EditSongModal({ song, onClose, onSave }: EditSongModalProps) {
  return (
    <Modal open={Boolean(song)} onClose={onClose} ariaLabel="Edit song">
      {/* Keyed by id so the draft fields reset when a different song is opened. */}
      {song && <EditSongForm key={song.id} song={song} onClose={onClose} onSave={onSave} />}
    </Modal>
  );
}
